import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** The title of the one flow component header — the `h3` below draws it. */
export const FLOW_COMPONENT_TITLE_CLASS = "text-lg font-semibold";

/**
 * A heading inside flow markdown (a text block, a modal body): the same title,
 * plus the margins that set it off from the paragraph above. `FlowMarkdown`
 * renders with it and the markdown editor's single-heading theme draws with it,
 * so the editor shows exactly what the flow page will. QL-296.
 */
export const FLOW_MARKDOWN_HEADING_CLASS = `${FLOW_COMPONENT_TITLE_CLASS} mt-4 mb-1 first:mt-0`;

interface FlowComponentHeaderProps {
  /** The component's configured title. */
  title?: ReactNode;
  /** The component's configured helper text, directly under the title. */
  description?: ReactNode;
  /** Inline extra next to the title (a "Test" badge, a status pill). */
  titleAddon?: ReactNode;
  className?: string;
}

/**
 * The one header every flow component renders above its body (DESIGN.md ›
 * Components › Flow component headers): `text-lg font-semibold` title, `text-sm`
 * muted description, paired at `space-y-1`. Renders nothing when the component
 * has neither, so a widget can always mount it unconditionally. QL-296.
 */
export function FlowComponentHeader({
  title,
  description,
  titleAddon,
  className,
}: FlowComponentHeaderProps) {
  if (!title && !description) return null;
  return (
    <div className={cn("space-y-1", className)}>
      {title &&
        (titleAddon ? (
          <div className="flex items-center gap-2">
            <h3 className={FLOW_COMPONENT_TITLE_CLASS}>{title}</h3>
            {titleAddon}
          </div>
        ) : (
          <h3 className={FLOW_COMPONENT_TITLE_CLASS}>{title}</h3>
        ))}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}
